import { useState } from 'react';
import { Search, BookOpen, Target, Loader2, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

/**
 * Page de test pour récupérer les compétences ROME 4.0
 *
 * Cette page permet de :
 * 1. Rechercher un RNCP et trouver ses codes ROME associés
 * 2. Récupérer les compétences détaillées depuis l'API France Travail ROME 4.0
 * 3. Afficher les compétences de manière structurée
 */
export default function RomeCompetencesTest() {
  const [rncpCode, setRncpCode] = useState('37537');
  const [loading, setLoading] = useState(false);
  const [romeCodes, setRomeCodes] = useState([]);
  const [competences, setCompetences] = useState({});
  const [selectedRome, setSelectedRome] = useState(null);

  /**
   * Étape 1 : Récupérer les codes ROME associés à un RNCP
   */
  const handleSearchRomeCodes = async () => {
    if (!rncpCode.trim()) {
      toast.error('Veuillez entrer un code RNCP');
      return;
    }

    setLoading(true);
    setRomeCodes([]);
    setCompetences({});
    setSelectedRome(null);

    try {
      // Appeler notre API pour trouver les mappings RNCP-ROME
      const response = await api.get(`/rncp-rome-mapping/by-rncp/${rncpCode}`);

      if (response.data.success && response.data.data.length > 0) {
        setRomeCodes(response.data.data);
        toast.success(`${response.data.data.length} code(s) ROME trouvé(s)`);
      } else {
        toast.error('Aucun code ROME trouvé pour ce RNCP');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la recherche des codes ROME');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Étape 2 : Récupérer les compétences détaillées depuis l'API ROME 4.0
   */
  const handleFetchCompetences = async (romeCode) => {
    setLoading(true);
    setSelectedRome(romeCode);

    try {
      // Appeler l'API France Travail pour récupérer les compétences
      const response = await api.get('/france-travail/competences', {
        params: {
          romeCode: romeCode
        }
      });

      if (response.data.success) {
        setCompetences(prev => ({
          ...prev,
          [romeCode]: response.data.data
        }));
        toast.success(`Compétences récupérées pour ${romeCode}`);
      } else {
        toast.error('Aucune compétence trouvée');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la récupération des compétences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* En-tête */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Test API ROME 4.0 Compétences
          </h1>
          <p className="text-gray-600">
            Récupération des compétences détaillées depuis l'API France Travail
          </p>
        </div>

        {/* Étape 1 : Recherche RNCP → ROME */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Search className="w-6 h-6 text-blue-600" />
            Étape 1 : Rechercher les codes ROME
          </h2>

          <div className="flex gap-3">
            <input
              type="text"
              value={rncpCode}
              onChange={(e) => setRncpCode(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchRomeCodes()}
              placeholder="Code RNCP (ex: 37537)"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={handleSearchRomeCodes}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              Rechercher
            </button>
          </div>

          {/* Résultats codes ROME */}
          {romeCodes.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold text-gray-900 mb-3">
                📋 Codes ROME associés ({romeCodes.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {romeCodes.map((mapping) => (
                  <div
                    key={mapping.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-bold text-lg text-blue-600">
                          {mapping.romeCode}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {mapping.romeLibelle}
                        </div>
                      </div>
                      {competences[mapping.romeCode] ? (
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : null}
                    </div>

                    <button
                      onClick={() => handleFetchCompetences(mapping.romeCode)}
                      disabled={loading && selectedRome === mapping.romeCode}
                      className="w-full mt-3 px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading && selectedRome === mapping.romeCode ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Chargement...
                        </>
                      ) : competences[mapping.romeCode] ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Rechargé
                        </>
                      ) : (
                        <>
                          <Target className="w-4 h-4" />
                          Récupérer les compétences
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Étape 2 : Affichage des compétences */}
        {Object.keys(competences).length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-green-600" />
              Étape 2 : Compétences détaillées
            </h2>

            <div className="space-y-6">
              {Object.entries(competences).map(([romeCode, compData]) => (
                <div key={romeCode} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-bold">
                      {romeCode}
                    </div>
                    <div className="text-gray-700">
                      {romeCodes.find(r => r.romeCode === romeCode)?.romeLibelle}
                    </div>
                  </div>

                  {/* Affichage des compétences */}
                  {Array.isArray(compData) && compData.length > 0 ? (
                    <div className="space-y-3">
                      <div className="text-sm text-gray-600 mb-2">
                        {compData.length} compétence(s) trouvée(s)
                      </div>
                      {compData.map((comp, idx) => (
                        <div
                          key={idx}
                          className="bg-gray-50 rounded-lg p-4 border-l-4 border-green-500"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-sm">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              {comp.code && (
                                <div className="text-xs text-gray-500 mb-1">
                                  Code: {comp.code}
                                </div>
                              )}
                              <div className="font-semibold text-gray-900">
                                {comp.libelle || comp.intitule || comp.title || 'Sans titre'}
                              </div>
                              {comp.description && (
                                <div className="text-sm text-gray-600 mt-2">
                                  {comp.description}
                                </div>
                              )}
                              {comp.type && (
                                <div className="inline-block mt-2 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                                  {comp.type}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 italic">
                      Aucune compétence détaillée disponible
                    </div>
                  )}

                  {/* Debug : Structure complète */}
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900">
                      🔍 Voir la structure JSON complète
                    </summary>
                    <pre className="mt-2 p-4 bg-gray-100 rounded text-xs overflow-auto max-h-96">
                      {JSON.stringify(compData, null, 2)}
                    </pre>
                  </details>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        {romeCodes.length === 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-bold text-blue-900 mb-2">
              💡 Instructions
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-blue-800">
              <li>Entrez un code RNCP (ex: 37537 pour Boulanger)</li>
              <li>Cliquez sur "Rechercher" pour trouver les codes ROME associés</li>
              <li>Pour chaque code ROME, cliquez sur "Récupérer les compétences"</li>
              <li>Les compétences détaillées s'afficheront en dessous</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
